import { getStripe, getPriceId } from '../utils/stripeConfig';
import { supabase } from '../utils/supabase';

const isDev = import.meta.env.DEV;

class PaymentService {
  constructor() {
    this.stripe = null;
  }

  async initializeStripe() {
    if (!this.stripe) {
      this.stripe = await getStripe();
    }
    return this.stripe;
  }

  // Create checkout session for subscription
  async createCheckoutSession({ 
    tier, 
    billingCycle = 'monthly', 
    userId, 
    successUrl = `${window.location.origin}/payment/success`, 
    cancelUrl = `${window.location.origin}/pricing` 
  }) {
    try {
      const priceId = getPriceId(tier, billingCycle);
      
      if (!priceId) {
        throw new Error(`Price ID not found for ${tier} ${billingCycle} plan. Please check your Stripe configuration.`);
      }

      isDev && console.log('Creating checkout session:', { tier, billingCycle, priceId, userId });

      // Get current session for JWT token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        isDev && console.error('Error getting session:', sessionError);
      }

      // Call Supabase Edge Function to create checkout session
      // The supabase.functions.invoke will automatically add the JWT token if user is logged in
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          userId,
          tier,
          billingCycle,
          successUrl,
          cancelUrl,
        },
        // Explicitly provide auth token if available
        headers: session?.access_token ? {
          'Authorization': `Bearer ${session.access_token}`
        } : {}
      });

      isDev && console.log('Supabase function response:', { data, error, hasSession: !!session });

      if (error) {
        isDev && console.error('Supabase function error:', error);
        // FunctionsHttpError.context is a Response object — read the body for the actual error
        let errorMessage = 'Failed to create checkout session';
        if (error.context && typeof error.context.json === 'function') {
          try {
            const errorBody = await error.context.json();
            isDev && console.error('Edge function error body:', errorBody);
            errorMessage = errorBody.error || errorBody.message || errorMessage;
          } catch {
            errorMessage = error.message || errorMessage;
          }
        } else {
          errorMessage = error.message || errorMessage;
        }
        throw new Error(errorMessage);
      }

      if (!data) {
        throw new Error('No response data received from checkout session creation');
      }

      if (data.error) {
        isDev && console.error('Edge function returned error:', data);
        throw new Error(data.error);
      }

      isDev && console.log('Checkout session created:', data);
      return data;
    } catch (error) {
      isDev && console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  // Redirect to Stripe Checkout using the session URL
  async redirectToCheckout(sessionUrl) {
    try {
      if (!sessionUrl) {
        throw new Error('No checkout session URL provided');
      }

      // Use window.location.href for modern Stripe.js compatibility
      window.location.href = sessionUrl;
    } catch (error) {
      isDev && console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  // Create subscription checkout flow
  async subscribeToplan({ tier, billingCycle = 'monthly', userId }) {
    try {
      // Create checkout session with explicit success and cancel URLs
      const session = await this.createCheckoutSession({
        tier,
        billingCycle,
        userId,
        successUrl: `${window.location.origin}/payment/success`,
        cancelUrl: `${window.location.origin}/pricing`,
      });

      // Redirect to Stripe Checkout using the session URL
      await this.redirectToCheckout(session.url);
    } catch (error) {
      isDev && console.error('Error subscribing to plan:', error);
      throw error;
    }
  }

  // Create customer portal session for subscription management
  async createPortalSession(userId, returnUrl = window.location.origin) {
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          userId,
          returnUrl,
        },
      });

      if (error) throw error;

      return data;
    } catch (error) {
      isDev && console.error('Error creating portal session:', error);
      throw error;
    }
  }

  // Redirect to customer portal
  async redirectToPortal(userId) {
    try {
      const portal = await this.createPortalSession(userId);
      window.location.href = portal.url;
    } catch (error) {
      isDev && console.error('Error redirecting to portal:', error);
      throw error;
    }
  }

  // Get subscription status
  async getSubscriptionStatus(userId) {
    try {
      const { data, error } = await supabase.functions.invoke('get-subscription-status', {
        body: { userId },
      });

      if (error) throw error;

      return data;
    } catch (error) {
      isDev && console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  // Cancel subscription
  async cancelSubscription(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required to cancel subscription');
      }

      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { userId },
      });

      if (error) throw error;

      return data;
    } catch (error) {
      isDev && console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  // Update subscription
  async updateSubscription({ subscriptionId, newPriceId }) {
    try {
      const { data, error } = await supabase.functions.invoke('update-subscription', {
        body: { 
          subscriptionId, 
          newPriceId 
        },
      });

      if (error) throw error;

      return data;
    } catch (error) {
      isDev && console.error('Error updating subscription:', error);
      throw error;
    }
  }

  // Validate payment method
  async validatePaymentMethod(paymentMethodId) {
    try {
      const stripe = await this.initializeStripe();
      
      if (!stripe) {
        throw new Error('Stripe not initialized');
      }

      const { paymentMethod, error } = await stripe.retrievePaymentMethod(paymentMethodId);

      if (error) {
        throw error;
      }

      return paymentMethod;
    } catch (error) {
      isDev && console.error('Error validating payment method:', error);
      throw error;
    }
  }

  // Format currency for display
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100); // Stripe amounts are in cents
  }

  // Calculate savings for annual billing
  calculateAnnualSavings(monthlyPrice, annualPrice) {
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - annualPrice;
    const percentage = Math.round((savings / monthlyTotal) * 100);
    
    return {
      amount: savings,
      percentage,
      formattedAmount: this.formatCurrency(savings),
    };
  }
}

// Export singleton instance
export const paymentService = new PaymentService();
export default paymentService;